import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import {
  Search as SearchIcon,
  Database,
  Loader2,
  Filter,
  FilterX,
  X,
  Sparkles,
  CheckCircle2,
  Tag,
  Info,
  Activity,
} from 'lucide-react';
import { apiFetch } from '../lib/apiFetch';
import { useApiRoot } from '../contexts/ApiRootContext';
import { useToast } from '../contexts/ToastContext';
import AddToGroupModal from '../components/AddToGroupModal';
import { formatGroupLabel } from '../utils/groupLabel';
import { addedToGroupMessage } from '../utils/groupMembership';
import { getFileTypeFilterKey } from '../utils/fileUtils';
import Tooltip from '../components/Tooltip';
import AppNavActions from '../components/AppNavActions';
import FeedbackThumbs from '../components/FeedbackThumbs';
import { db } from '../firebase';
import { BRAND_BG, BRAND_TEXT } from '../components/search/constants';
import { capitalize } from '../components/search/searchFormat';
import { buildAiAutoFilterSections } from '../components/search/searchAiAutoFilters';
import { buildSearchFeedbackFields } from '../components/search/searchFeedback';
import { groupResults } from '../components/search/searchGrouping';
import {
  getAvailableSortOptions,
  getDefaultSortDirection,
  getDefaultSortOption,
  sortGroupedResults,
  sortResults,
} from '../components/search/searchSorting';
import { getResultsPerPage, readStoredViewMode, writeStoredViewMode } from '../components/search/searchViewMode';
import SidebarSection from '../components/search/SidebarSection';
import AiAutoFiltersPanel from '../components/search/AiAutoFiltersPanel';
import FilterPanel from '../components/search/FilterPanel';
import GroupingControls from '../components/search/GroupingControls';
import SortControl from '../components/search/SortControl';
import ViewModeToggle from '../components/search/ViewModeToggle';
import ResultCard from '../components/search/ResultCard';
import ResultRow from '../components/search/ResultRow';
import GroupedResults from '../components/search/GroupedResults';
import QueryDiagnosticsPanel from '../components/search/QueryDiagnosticsPanel';
import Pagination from '../components/search/Pagination';
import RecentSearchInput from '../components/search/RecentSearchInput';
import SelectionBar from '../components/search/SelectionBar';
import {
  collectResultIds,
  toggleRecordSelection,
  toggleVisibleSelection,
} from '../components/search/resultSelection';
import type { ResultSelectionControls } from '../components/search/resultSelection';
import { ISC_LEVELS } from '../constants/record';
import type {
  RecentSearch,
  OmittedResultsData,
  SearchResult,
  SearchResponseData,
  SearchLinks,
  SearchProgressData,
  ResultGroupBy,
  ClassificationGroupDepth,
  SortOption,
  SortDirection,
  ResultViewMode,
} from '../components/search/types';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SEARCH_CACHE_KEY = 'ara_search_cache';
const SEARCH_RERUN_PARAM = 'rerun';

type SearchFilters = {
  fileTypes: string[];
  iscLevels: string[];
};

const EMPTY_FILTERS: SearchFilters = { fileTypes: [], iscLevels: [] };

function getResultIscLevel(result: SearchResult): string {
  return result.information_security_classification?.level || 'unclassified';
}

function uniqueIscLevels(results: SearchResult[]): string[] {
  const present = new Set(results.map(getResultIscLevel));
  return [
    ...ISC_LEVELS.filter((level) => present.has(level)),
    ...[...present].filter((level) => !ISC_LEVELS.includes(level)).sort(),
  ];
}

function normalizeFilters(filters?: Partial<SearchFilters> | null): SearchFilters {
  return {
    fileTypes: filters?.fileTypes ?? [],
    iscLevels: filters?.iscLevels ?? [],
  };
}

type SearchSessionCache = {
  query: string;
  data: SearchResponseData;
  links: SearchLinks;
  omittedResults: SearchResult[] | null;
  filters: SearchFilters;
  groupBy: ResultGroupBy;
  classificationGroupDepth: ClassificationGroupDepth;
  sortBy: SortOption;
  sortDirection: SortDirection;
  currentPage: number;
  scrollY: number;
};

function readSearchSessionCache(): SearchSessionCache | null {
  try {
    const raw = sessionStorage.getItem(SEARCH_CACHE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as SearchSessionCache;
  } catch {
    return null;
  }
}

function writeSearchSessionCache(payload: SearchSessionCache): void {
  try {
    sessionStorage.setItem(SEARCH_CACHE_KEY, JSON.stringify(payload));
  } catch {
    /* can be ignored */
  }
}

const STEP_LABELS: Record<string, string> = {
  normalise_query: 'Normalising query',
  spell_checking: 'Checking spelling',
  entity_recognition: 'Recognising entities',
  geographical_resolution: 'Resolving address',
  apply_entity_filters: 'Applying entity filters',
  inspecting_query: 'Inspecting query',
  query_enhancement: 'Improving query',
  retrieval_embedding: 'Understanding intention',
  semantic_search: 'Searching records',
  filter_search: 'Searching records',
  information_security_classification_filtering: 'Filtering by security clearance',
  reranking: 'Reranking results',
  dynamic_cutoff: 'Applying relevance filter',
};

function createSearchId(): string {
  const cryptoApi = globalThis.crypto;
  if (cryptoApi?.randomUUID) {
    return cryptoApi.randomUUID();
  }

  return '10000000-1000-4000-8000-100000000000'.replace(/[018]/g, (c) =>
    (
      Number(c) ^
      ((cryptoApi?.getRandomValues(new Uint8Array(1))[0] ?? Math.floor(Math.random() * 256)) &
        (15 >> (Number(c) / 4)))
    ).toString(16)
  );
}

function getErrorMessage(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback;
}

// ---------------------------------------------------------------------------
// Navbar
// ---------------------------------------------------------------------------

const Navbar = ({
  navigate,
  compact,
  query,
  onQueryChange,
  onSearch,
  recentSearches,
  onRequestRecentSearches,
  onSelectRecentSearch,
}: {
  navigate: (path: string) => void;
  compact: boolean;
  query?: string;
  onQueryChange?: (q: string) => void;
  onSearch?: () => void;
  recentSearches?: RecentSearch[];
  onRequestRecentSearches?: (query?: string) => void;
  onSelectRecentSearch?: (query: string) => void;
}) => (
  <nav className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-gray-100">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex justify-between h-20 items-center gap-4">
        <div
          className="flex items-center space-x-3 group cursor-pointer shrink-0"
          onClick={() => navigate('/')}
        >
          <div
            className={`w-10 h-10 ${BRAND_BG} rounded-xl shadow-lg shadow-emerald-200 flex items-center justify-center text-white transform group-hover:scale-110 transition-transform duration-300`}
          >
            <Database className="w-5 h-5" />
          </div>
          <div>
            <span className={`text-xl font-bold tracking-tight ${BRAND_TEXT}`}>ARA</span>
            <span className="text-xs text-gray-400 block font-medium -mt-1 tracking-widest opacity-60">
              Automated Records Assistant
            </span>
          </div>
        </div>

        {compact &&
          query !== undefined &&
          onQueryChange &&
          onSearch &&
          recentSearches &&
          onRequestRecentSearches &&
          onSelectRecentSearch && (
            <div className="flex-1 max-w-2xl">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  onSearch();
                }}
                className="relative"
              >
                <RecentSearchInput
                  value={query}
                  recentSearches={recentSearches}
                  onChange={onQueryChange}
                  onRequestRecentSearches={onRequestRecentSearches}
                  onSelect={onSelectRecentSearch}
                  inputClassName="w-full pl-12 pr-12 py-3 bg-white border border-gray-200 rounded-full shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent hover:shadow-md transition-shadow"
                  iconClassName="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"
                  showClearButton
                />
              </form>
            </div>
          )}

        <AppNavActions />
      </div>
    </div>
  </nav>
);

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export default function Search() {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { getHref } = useApiRoot();
  const recommendationsHref = getHref('search_recommendations');
  const [searchParams, setSearchParams] = useSearchParams();
  const pendingScrollYRef = useRef<number | null>(null);

  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [searchData, setSearchData] = useState<SearchResponseData | null>(null);
  const [searchLinks, setSearchLinks] = useState<SearchLinks>({});
  const canAddToGroup = Boolean(searchLinks.add_to_group);
  // Null until the omitted results are fetched, which is also what hides the
  // notice: once they are shown there is nothing left to offer.
  const [omittedResults, setOmittedResults] = useState<SearchResult[] | null>(null);
  const [omittedResultsLoading, setOmittedResultsLoading] = useState(false);
  const [omittedResultsError, setOmittedResultsError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeSearchId, setActiveSearchId] = useState<string | null>(null);
  const [searchProgress, setSearchProgress] = useState<SearchProgressData | null>(null);
  const [searchProgressError, setSearchProgressError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [groupBy, setGroupByState] = useState<ResultGroupBy>('none');
  const [classificationGroupDepth, setClassificationGroupDepthState] = useState<ClassificationGroupDepth>(3);
  const [sortBy, setSortByState] = useState<SortOption>(getDefaultSortOption(null));
  const [sortDirection, setSortDirectionState] = useState<SortDirection>(
    getDefaultSortDirection(getDefaultSortOption(null))
  );
  // Independent of any single search's session cache — a browser-wide
  // preference, not reset by a new search the way sort/filters/grouping are.
  const [viewMode, setViewModeState] = useState<ResultViewMode>(() => readStoredViewMode());
  const resultsPerPage = getResultsPerPage(viewMode);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [selectionAnchorId, setSelectionAnchorId] = useState<string | null>(null);
  const [addToGroupOpen, setAddToGroupOpen] = useState(false);
  const [trackedSelectionScope, setTrackedSelectionScope] = useState<string | null>(null);
  const [recentSearches, setRecentSearches] = useState<RecentSearch[]>([]);
  const unfilteredRecentSearchesRef = useRef<RecentSearch[] | null>(null);
  const recentSearchesRequestIdRef = useRef(0);

  // Filters
  const [filters, setFilters] = useState<SearchFilters>(EMPTY_FILTERS);

  const toggleFilterValue = useCallback((key: keyof SearchFilters, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [key]: prev[key].includes(value)
        ? prev[key].filter((item) => item !== value)
        : [...prev[key], value],
    }));
    setCurrentPage(1);
  }, []);

  const toggleFileTypeFilter = useCallback(
    (value: string) => toggleFilterValue('fileTypes', value),
    [toggleFilterValue]
  );

  const toggleIscLevelFilter = useCallback(
    (value: string) => toggleFilterValue('iscLevels', value),
    [toggleFilterValue]
  );

  const clearFilters = useCallback(() => {
    setFilters(EMPTY_FILTERS);
    setCurrentPage(1);
  }, []);

  const setGroupBy = useCallback((value: ResultGroupBy) => {
    setGroupByState(value);
    setCurrentPage(1);
  }, []);

  const setClassificationGroupDepth = useCallback((value: ClassificationGroupDepth) => {
    setClassificationGroupDepthState(value);
    setCurrentPage(1);
  }, []);

  const clearGrouping = useCallback(() => {
    setGroupByState('none');
    setClassificationGroupDepthState(3);
    setCurrentPage(1);
  }, []);

  const setSortBy = useCallback((value: SortOption) => {
    setSortByState(value);
    setSortDirectionState(getDefaultSortDirection(value)); // 'asc'/'desc' mean different things per option
    setCurrentPage(1);
  }, []);

  const toggleSortDirection = useCallback(() => {
    setSortDirectionState((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    setCurrentPage(1);
  }, []);

  const resetOmittedResults = useCallback(() => {
    setOmittedResults(null);
    setOmittedResultsLoading(false);
    setOmittedResultsError(null);
  }, []);

  const activeFilterCount = filters.fileTypes.length + filters.iscLevels.length;

  const hasSearched = searchData !== null;
  const visibleProgressSteps = useMemo(() => {
    const steps = searchProgress?.steps?.all_steps ?? [];
    return steps.filter(
      (step, index) => step.step && steps.findIndex((candidate) => candidate.step === step.step) === index
    );
  }, [searchProgress]);
  const currentProgressStep =
    searchProgress?.steps?.current_step ??
    visibleProgressSteps[visibleProgressSteps.length - 1]?.step ??
    null;

  useEffect(() => {
    if (!activeSearchId || !loading) return;

    const unsubscribe = onSnapshot(
      doc(db, 'search', activeSearchId),
      (snapshot) => {
        setSearchProgress((snapshot.data() as SearchProgressData | undefined) ?? null);
        setSearchProgressError(null);
      },
      (err) => {
        console.error('Error fetching search progress:', err);
        setSearchProgressError('Live search progress is unavailable.');
      }
    );

    return unsubscribe;
  }, [activeSearchId, loading]);

  const updateSearchLocation = useCallback((q: string) => {
    setSearchParams({ q });
  }, [setSearchParams]);

  const persistSearchSession = useCallback(
    (
      data: SearchResponseData,
      options?: {
        query?: string;
        links?: SearchLinks;
        omittedResults?: SearchResult[] | null;
        filters?: SearchFilters;
        groupBy?: ResultGroupBy;
        classificationGroupDepth?: ClassificationGroupDepth;
        sortBy?: SortOption;
        sortDirection?: SortDirection;
        currentPage?: number;
        scrollY?: number;
      }
    ) => {
      const q = (options?.query ?? searchParams.get('q') ?? query).trim();
      if (!q) return;
      const existing = readSearchSessionCache();
      writeSearchSessionCache({
        query: q,
        data,
        links: options?.links ?? searchLinks,
        omittedResults: options?.omittedResults !== undefined ? options.omittedResults : omittedResults,
        filters: options?.filters ?? filters,
        groupBy: options?.groupBy ?? groupBy,
        classificationGroupDepth: options?.classificationGroupDepth ?? classificationGroupDepth,
        sortBy: options?.sortBy ?? sortBy,
        sortDirection: options?.sortDirection ?? sortDirection,
        currentPage: options?.currentPage ?? currentPage,
        scrollY: options?.scrollY ?? existing?.scrollY ?? window.scrollY,
      });
    },
    [
      searchParams,
      query,
      searchLinks,
      omittedResults,
      filters,
      groupBy,
      classificationGroupDepth,
      sortBy,
      sortDirection,
      currentPage,
    ]
  );

  const navigateFromSearch = useCallback(
    (path: string) => {
      if (searchData) {
        persistSearchSession(searchData, { scrollY: window.scrollY });
      }
      navigate(path);
    },
    [navigate, persistSearchSession, searchData]
  );

  // -----------------------------------------------------------------------
  // Search execution
  // -----------------------------------------------------------------------

  const performSearch = useCallback(async (q: string) => {
    const searchId = createSearchId();
    setLoading(true);
    setError(null);
    setSearchData(null);
    setSearchLinks({});
    resetOmittedResults();
    setActiveSearchId(searchId);
    setSearchProgress(null);
    setSearchProgressError(null);
    setCurrentPage(1);
    clearFilters();
    clearGrouping();

    try {
      const response = await apiFetch('/api/v1/search', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: q, search_id: searchId }),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || `HTTP ${response.status}`);
      }

      const data = await response.json();
      const searchResult = data._embedded.search as SearchResponseData;
      const links = (data._links ?? {}) as SearchLinks;
      const defaultSort = getDefaultSortOption(searchResult.retrieval?.mode);
      const defaultDirection = getDefaultSortDirection(defaultSort);
      setSearchData(searchResult);
      setSearchLinks(links);
      setSortByState(defaultSort);
      setSortDirectionState(defaultDirection);
      persistSearchSession(searchResult, {
        query: q,
        links,
        omittedResults: null,
        filters: EMPTY_FILTERS,
        groupBy: 'none',
        classificationGroupDepth: 3,
        sortBy: defaultSort,
        sortDirection: defaultDirection,
        currentPage: 1,
        scrollY: 0,
      });
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Search failed'));
    } finally {
      setLoading(false);
      setActiveSearchId(null);
    }
  }, [clearFilters, clearGrouping, persistSearchSession, resetOmittedResults]);

  const handleSearch = useCallback(() => {
    const q = query.trim();
    if (!q) return;
    updateSearchLocation(q);
    performSearch(q);
  }, [query, updateSearchLocation, performSearch]);

  const requestRecentSearches = useCallback(async (query?: string) => {
    if (!recommendationsHref) return;

    const trimmed = (query ?? '').trim();
    if (!trimmed && unfilteredRecentSearchesRef.current) {
      setRecentSearches(unfilteredRecentSearchesRef.current);
      return;
    }

    const requestId = ++recentSearchesRequestIdRef.current;
    const path = trimmed
      ? `${recommendationsHref}?query=${encodeURIComponent(trimmed)}`
      : recommendationsHref;

    try {
      const response = await apiFetch(path);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      const searches = data?._embedded?.search_recommendations;
      if (!Array.isArray(searches)) {
        throw new Error('Invalid recent searches response');
      }

      if (requestId !== recentSearchesRequestIdRef.current) return;

      setRecentSearches(searches as RecentSearch[]);
      if (!trimmed) {
        unfilteredRecentSearchesRef.current = searches as RecentSearch[];
      }
    } catch (err) {
      if (requestId === recentSearchesRequestIdRef.current) {
        console.error('Failed to fetch recent searches:', err);
      }
    }
  }, [recommendationsHref]);

  const handleRecentSearchSelect = useCallback(
    (selectedQuery: string) => {
      setQuery(selectedQuery);
      updateSearchLocation(selectedQuery);
      performSearch(selectedQuery);
    },
    [performSearch, updateSearchLocation]
  );

  const handleFeelingLucky = useCallback(async () => {
    const q = query.trim();
    if (!q) return;
    const searchId = createSearchId();
    setLoading(true);
    setError(null);
    setSearchData(null);
    setSearchLinks({});
    resetOmittedResults();
    setActiveSearchId(searchId);
    setSearchProgress(null);
    setSearchProgressError(null);
    clearGrouping();

    try {
      const response = await apiFetch('/api/v1/search', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: q, search_id: searchId }),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || `HTTP ${response.status}`);
      }

      const data = await response.json();
      const results = data._embedded.search.results;

      if (results?.length > 0) {
        navigate(`/records/${results[0].id}`);
        return;
      }

      updateSearchLocation(q);
      setSearchData(data._embedded.search);
      setSearchLinks((data._links ?? {}) as SearchLinks);
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Search failed'));
    } finally {
      setLoading(false);
      setActiveSearchId(null);
    }
  }, [query, navigate, updateSearchLocation, clearGrouping, resetOmittedResults]);

  const handleDownload = useCallback(
    async (recordId: string) => {
      try {
        const res = await apiFetch(`/api/v1/records/${recordId}/download`);
        if (!res.ok) throw new Error('Download failed');
        const data = await res.json();
        window.open(data.downloadUrl, '_blank');
      } catch {
        /* no-op */
      }
    },
    []
  );

  // Keep session cache in sync with filters/grouping/pagination while results are shown
  useEffect(() => {
    if (!searchData || loading) return;
    persistSearchSession(searchData);
  }, [
    searchData,
    loading,
    filters,
    groupBy,
    classificationGroupDepth,
    sortBy,
    sortDirection,
    currentPage,
    persistSearchSession,
  ]);

  // Restore scroll after session-cached results are painted
  useEffect(() => {
    if (!searchData || pendingScrollYRef.current == null) return;
    const scrollY = pendingScrollYRef.current;
    pendingScrollYRef.current = null;
    // Double rAF waits until layout reflects restored filters/page.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.scrollTo(0, scrollY);
      });
    });
  }, [searchData]);

  // Restore cached results or run search from URL on mount
  useEffect(() => {
    const q = searchParams.get('q');
    const forceRerun = searchParams.get(SEARCH_RERUN_PARAM) === '1';
    if (q && !searchData && !loading) {
      setQuery(q);
      if (forceRerun) {
        // Strip the one-shot flag so browser back later restores from session.
        setSearchParams({ q }, { replace: true });
        performSearch(q);
        return;
      }
      const cached = readSearchSessionCache();
      if (cached?.query === q && cached.data) {
        setSearchData(cached.data);
        setSearchLinks(cached.links ?? {});
        setOmittedResults(cached.omittedResults ?? null);
        if (cached.filters) setFilters(normalizeFilters(cached.filters));
        if (cached.groupBy) {
          setGroupByState(cached.groupBy);
        }
        if (cached.classificationGroupDepth) {
          setClassificationGroupDepthState(cached.classificationGroupDepth);
        }
        const restoredSortBy = cached.sortBy ?? getDefaultSortOption(cached.data.retrieval?.mode);
        setSortByState(restoredSortBy);
        setSortDirectionState(cached.sortDirection ?? getDefaultSortDirection(restoredSortBy));
        if (typeof cached.currentPage === 'number' && cached.currentPage > 0) {
          setCurrentPage(cached.currentPage);
        }
        pendingScrollYRef.current =
          typeof cached.scrollY === 'number' ? cached.scrollY : 0;
        return;
      }
      performSearch(q);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const rawQuery = (searchData?.raw_query || searchParams.get('q') || '').trim();
    if (rawQuery) {
      document.title = `ARA - ${rawQuery}`;
    } else {
      document.title = 'ARA';
    }
    return () => {
      document.title = 'ARA';
    };
  }, [searchData?.raw_query, searchParams]);

  // -----------------------------------------------------------------------
  // Derived data
  // -----------------------------------------------------------------------

  // The omitted results sit after the ones that survived the cutoff, which is
  // where their reranking scores already placed them.
  const allResults = useMemo(
    () => [...(searchData?.results ?? []), ...(omittedResults ?? [])],
    [searchData, omittedResults]
  );

  const filterOptions = useMemo(
    () => ({
      fileTypes: [
        ...new Set(
          allResults.map((r) => getFileTypeFilterKey(r.file?.content_type ?? '')).filter(Boolean)
        ),
      ] as string[],
      iscLevels: uniqueIscLevels(allResults),
    }),
    [allResults]
  );

  const filteredResults = useMemo(() => {
    let results = allResults;
    if (filters.fileTypes.length > 0) {
      results = results.filter((r) =>
        filters.fileTypes.includes(getFileTypeFilterKey(r.file?.content_type ?? ''))
      );
    }
    if (filters.iscLevels.length > 0) {
      results = results.filter((r) => filters.iscLevels.includes(getResultIscLevel(r)));
    }
    return results;
  }, [allResults, filters]);

  // Switching view mode changes the page size, which can shrink the total
  // page count — clamp so the page shown stays valid.
  const setViewMode = useCallback(
    (mode: ResultViewMode) => {
      writeStoredViewMode(mode);
      setViewModeState(mode);
      const newTotalPages = Math.max(1, Math.ceil(filteredResults.length / getResultsPerPage(mode)));
      setCurrentPage((prev) => Math.min(prev, newTotalPages));
    },
    [filteredResults.length]
  );

  const availableSortOptions = useMemo(
    () => getAvailableSortOptions(searchData?.retrieval?.mode),
    [searchData]
  );

  // Kept separate from filteredResults so group order (derived from the
  // backend's own ordering) stays stable regardless of the chosen sort.
  const sortedResults = useMemo(
    () => sortResults(filteredResults, sortBy, sortDirection),
    [filteredResults, sortBy, sortDirection]
  );

  const { appliedFilters, unappliedDetections } = useMemo(
    () =>
      buildAiAutoFilterSections(
        searchData?.pre_retrieval?.geographical_resolution,
        searchData?.pre_retrieval?.entity_recognition?.detected_email_addresses,
        searchData?.pre_retrieval?.entity_recognition?.detected_ozone_ids
      ),
    [searchData]
  );
  const appliedFilterValueCount = appliedFilters.reduce(
    (total, item) => total + item.values.length,
    0
  );

  const iscExcludedCount =
    searchData?.post_retrieval?.information_security_classification_filtering?.excluded_count ?? 0;

  const totalPages = Math.ceil(filteredResults.length / resultsPerPage);
  const paginatedResults = sortedResults.slice(
    (currentPage - 1) * resultsPerPage,
    currentPage * resultsPerPage
  );
  const groupingActive = groupBy !== 'none';
  const omittedResultCount =
    searchData?.post_retrieval?.dynamic_filtering?.excluded_count ?? 0;
  // Grouping renders every result at once, so it has no "last page" to wait for.
  const showOmittedResultsAction =
    Boolean(searchLinks.omitted_results) &&
    omittedResults === null &&
    (groupingActive || currentPage >= totalPages);
  const groupedResults = useMemo(
    () =>
      sortGroupedResults(
        groupResults(filteredResults, groupBy, classificationGroupDepth),
        sortBy,
        sortDirection
      ),
    [filteredResults, groupBy, classificationGroupDepth, sortBy, sortDirection]
  );
  const displayedResultCount = filteredResults.length;
  const searchFeedbackFields = useMemo(
    () => (searchData ? buildSearchFeedbackFields(searchData, filteredResults, appliedFilters) : []),
    [searchData, filteredResults, appliedFilters]
  );

  // Declared with the derived data rather than the other handlers because it
  // needs to know how many results are on show before it adds to them.
  //
  // The cutoff recorded what it trimmed, so these are read back from the
  // search that produced them rather than found again by a second search.
  const handleShowOmittedResults = useCallback(async () => {
    const href = searchLinks.omitted_results?.href;
    if (!href || !searchData) return;

    setOmittedResultsLoading(true);
    setOmittedResultsError(null);

    try {
      const response = await apiFetch(href);
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || `HTTP ${response.status}`);
      }

      const data = await response.json();
      const results = (data._embedded.omitted_results as OmittedResultsData).results ?? [];
      setOmittedResults(results);
      // The omitted results land after the ones already on show, which on an
      // exactly-full last page is the page after this one.
      setCurrentPage(Math.floor(displayedResultCount / resultsPerPage) + 1);
      persistSearchSession(searchData, { omittedResults: results });
    } catch (err: unknown) {
      setOmittedResultsError(getErrorMessage(err, 'Failed to load the filtered results'));
    } finally {
      setOmittedResultsLoading(false);
    }
  }, [
    searchLinks.omitted_results?.href,
    searchData,
    displayedResultCount,
    resultsPerPage,
    persistSearchSession,
  ]);

  // Selection belongs to the results currently on screen. A new query, filter,
  // sort, page, or view clears it before paint.
  const visibleResultIds = groupingActive
    ? collectResultIds(groupedResults)
    : paginatedResults.map((result) => result.id);
  const selectionScope = [
    searchData?.id ?? '',
    query,
    filters.fileTypes.join('\0'),
    filters.iscLevels.join('\0'),
    groupBy,
    String(classificationGroupDepth),
    sortBy,
    sortDirection,
    viewMode,
    String(currentPage),
    omittedResults === null ? 'omitted-hidden' : `omitted-${omittedResults.length}`,
  ].join('|');
  const selectionIsCurrent = trackedSelectionScope === selectionScope;
  if (!selectionIsCurrent) {
    setTrackedSelectionScope(selectionScope);
    setSelectedIds(new Set());
    setSelectionAnchorId(null);
    setAddToGroupOpen(false);
  }
  const activeSelectedIds = useMemo(
    () => (selectionIsCurrent && canAddToGroup ? selectedIds : new Set<string>()),
    [selectionIsCurrent, selectedIds, canAddToGroup],
  );
  const selectedRecordIds = visibleResultIds.filter((id) => activeSelectedIds.has(id));

  const handleToggleResult = useCallback((recordId: string, shiftKey: boolean) => {
    const outcome = toggleRecordSelection(
      visibleResultIds,
      activeSelectedIds,
      selectionAnchorId,
      recordId,
      shiftKey,
    );
    setSelectedIds(outcome.selectedIds);
    setSelectionAnchorId(outcome.anchorId);
  }, [visibleResultIds, activeSelectedIds, selectionAnchorId]);

  const handleToggleVisible = useCallback(() => {
    setSelectedIds(toggleVisibleSelection(visibleResultIds, activeSelectedIds));
    setSelectionAnchorId(null);
  }, [visibleResultIds, activeSelectedIds]);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
    setSelectionAnchorId(null);
    setAddToGroupOpen(false);
  }, []);

  const selection: ResultSelectionControls = useMemo(() => ({
    enabled: canAddToGroup,
    active: selectedRecordIds.length > 0,
    isSelected: (recordId: string) => activeSelectedIds.has(recordId),
    onToggle: handleToggleResult,
  }), [canAddToGroup, selectedRecordIds.length, activeSelectedIds, handleToggleResult]);

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  // Landing view – Google-style centered search
  if (!hasSearched && !loading) {
    return (
      <div className="flex-1 bg-white text-gray-900 font-sans selection:bg-emerald-100 selection:text-emerald-900 flex flex-col">
        <Navbar navigate={navigate} compact={false} />

        <main className="flex-1 flex flex-col items-center justify-center px-4 -mt-20">

          {/* Search bar */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSearch();
            }}
            className="w-full max-w-xl"
          >
            <RecentSearchInput
              value={query}
              recentSearches={recentSearches}
              onChange={setQuery}
              onRequestRecentSearches={requestRecentSearches}
              onSelect={handleRecentSearchSelect}
              inputClassName="w-full pl-14 pr-5 py-4 bg-white border border-gray-200 rounded-full shadow-sm text-base focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent hover:shadow-lg transition-shadow"
              iconClassName="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"
            />

            <div className="flex items-center justify-center gap-3 mt-6">
              <button
                type="submit"
                className={`px-6 py-2.5 ${BRAND_BG} text-white text-sm font-medium rounded-lg hover:bg-emerald-700 active:bg-emerald-900 transition-colors shadow-sm`}
              >
                Search
              </button>
              <button
                type="button"
                onClick={handleFeelingLucky}
                className="px-6 py-2.5 bg-gray-50 border border-gray-200 text-gray-600 text-sm font-medium rounded-lg hover:border-gray-300 hover:bg-gray-100 active:bg-gray-200 transition-colors flex items-center gap-2"
              >
                <Sparkles className="w-4 h-4" />
                I'm Feeling Lucky
              </button>
            </div>
          </form>

          {error && (
            <div className="mt-6 max-w-xl w-full bg-red-50 text-red-700 border border-red-200 rounded-xl px-5 py-3 text-sm">
              {error}
            </div>
          )}
        </main>

        <footer className="bg-white border-t border-gray-200 py-6">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-sm text-gray-500">
            <p>&copy; 2026 Palmerston North City Council &bull; ARA</p>
          </div>
        </footer>
      </div>
    );
  }

  // Loading view
  if (loading) {
    return (
      <div className="flex-1 bg-white text-gray-900 font-sans flex flex-col">
        <Navbar navigate={navigate} compact={false} />
        <main className="flex-1 flex flex-col items-center justify-center px-4 -mt-20">
          <div className="flex flex-col items-center gap-6">
            <div className="relative">
              <div className={`w-16 h-16 ${BRAND_BG} rounded-2xl flex items-center justify-center text-white`}>
                <SearchIcon className="w-8 h-8" />
              </div>
              <Loader2 className="absolute -top-2 -right-2 w-6 h-6 text-emerald-500 animate-spin" />
            </div>
            <div className="text-center">
              <p className="text-lg font-semibold text-gray-700">
                Searching records...
              </p>
              <p className="text-sm text-gray-400 mt-1">
                {currentProgressStep
                  ? STEP_LABELS[currentProgressStep] || capitalize(currentProgressStep)
                  : 'Starting semantic search pipeline'}
              </p>
            </div>
            <div className="w-80 sm:w-96 rounded-xl border border-gray-100 bg-gray-50/80 px-4 py-3">
              {visibleProgressSteps.length > 0 ? (
                <ol className="space-y-2">
                  {visibleProgressSteps.map((step, index) => {
                    const isCurrent = step.step === currentProgressStep;
                    const label = STEP_LABELS[step.step] || capitalize(step.step);
                    return (
                      <li key={step.step} className="flex items-center gap-3 text-sm">
                        <span
                          className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${
                            isCurrent ? 'bg-emerald-100' : 'bg-emerald-50'
                          }`}
                        >
                          {isCurrent ? (
                            <span className="h-2.5 w-2.5 rounded-full bg-emerald-600 animate-pulse" />
                          ) : (
                            <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" aria-hidden />
                          )}
                        </span>
                        <span className={`min-w-0 flex-1 text-left ${isCurrent ? 'font-medium text-gray-800' : 'text-gray-500'}`}>
                          {label}
                        </span>
                        {!isCurrent && index < visibleProgressSteps.length - 1 && (
                          <span className="shrink-0 text-xs text-emerald-600">Done</span>
                        )}
                      </li>
                    );
                  })}
                </ol>
              ) : (
                <div className="flex items-center gap-3 text-sm text-gray-500">
                  <span className="h-2.5 w-2.5 rounded-full bg-emerald-600 animate-pulse" />
                  Preparing search
                </div>
              )}
              {searchProgressError && (
                <p className="mt-3 text-xs text-amber-700">{searchProgressError}</p>
              )}
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Results view
  return (
    <div className="flex-1 bg-gray-50/50 text-gray-900 font-sans selection:bg-emerald-100 selection:text-emerald-900 flex flex-col">
      <Navbar
        navigate={navigate}
        compact
        query={query}
        onQueryChange={setQuery}
        onSearch={handleSearch}
        recentSearches={recentSearches}
        onRequestRecentSearches={requestRecentSearches}
        onSelectRecentSearch={handleRecentSearchSelect}
      />

      <main className="flex-1 max-w-6xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Sidebar: Auto Filters / Filter / Group */}
          {searchData && (
            <aside className="lg:w-64 shrink-0 space-y-3">
              <SidebarSection
                title="AI auto filters"
                icon={<Sparkles className="w-4 h-4 text-gray-400" />}
                badge={
                  appliedFilterValueCount > 0 && (
                    <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500">
                      {appliedFilterValueCount}
                    </span>
                  )
                }
              >
                <AiAutoFiltersPanel
                  appliedFilters={appliedFilters}
                  unappliedDetections={unappliedDetections}
                />
              </SidebarSection>

              <SidebarSection
                title="Filters"
                icon={<Filter className="w-4 h-4 text-gray-400" />}
                defaultOpen={false}
                badge={
                  activeFilterCount > 0 && (
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        clearFilters();
                      }}
                      className="flex items-center gap-1 text-xs text-red-500 hover:text-red-700"
                    >
                      <X className="w-3 h-3" />
                      Clear
                    </button>
                  )
                }
              >
                <FilterPanel
                  fileTypes={filterOptions.fileTypes}
                  selectedFileTypes={filters.fileTypes}
                  onToggleFileType={toggleFileTypeFilter}
                  iscLevels={filterOptions.iscLevels}
                  selectedIscLevels={filters.iscLevels}
                  onToggleIscLevel={toggleIscLevelFilter}
                />
              </SidebarSection>

              <SidebarSection title="Group" icon={<Tag className="w-4 h-4 text-gray-400" />} defaultOpen={false}>
                <GroupingControls
                  groupBy={groupBy}
                  classificationGroupDepth={classificationGroupDepth}
                  setGroupBy={setGroupBy}
                  setClassificationGroupDepth={setClassificationGroupDepth}
                />
              </SidebarSection>

              <SidebarSection
                title="Query diagnostics"
                icon={<Activity className="w-4 h-4 text-gray-400" />}
                defaultOpen={false}
              >
                <QueryDiagnosticsPanel data={searchData} />
              </SidebarSection>
            </aside>
          )}

          {/* Main results column */}
          <div className="flex-1 min-w-0">
            <div className="mb-3 space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <div className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm">
                  <span className="font-semibold text-gray-700">{displayedResultCount}</span>
                  <span className="text-gray-500">Result{displayedResultCount !== 1 ? 's' : ''}</span>

                  {iscExcludedCount > 0 && (
                    <Tooltip content="Excluded because these results are above your information security clearance level">
                      <span className="flex items-center gap-1.5 text-gray-500 cursor-default">
                        <span className="text-gray-300">-</span>
                        {iscExcludedCount} result{iscExcludedCount !== 1 ? 's' : ''} excluded
                        <Info className="w-3.5 h-3.5 text-red-500" />
                      </span>
                    </Tooltip>
                  )}
                </div>

                {searchData && (
                  <SortControl
                    sortBy={sortBy}
                    direction={sortDirection}
                    options={availableSortOptions}
                    onChange={setSortBy}
                    onToggleDirection={toggleSortDirection}
                  />
                )}

                {searchData && <ViewModeToggle viewMode={viewMode} onChange={setViewMode} />}

                <div className="ml-auto flex items-center gap-2">
                  {searchData && (
                    <FeedbackThumbs
                      key={searchData.id}
                      surface="search"
                      targetId={searchData.id}
                      fields={searchFeedbackFields}
                    />
                  )}
                </div>
              </div>

              {canAddToGroup && selectedRecordIds.length > 0 && (
                <SelectionBar
                  selectedCount={selectedRecordIds.length}
                  allSelected={selectedRecordIds.length === visibleResultIds.length}
                  selectAllLabel={groupingActive ? 'Select all results' : 'Select all on this page'}
                  canAddToGroup={canAddToGroup}
                  onToggleVisible={handleToggleVisible}
                  onAddToGroup={() => setAddToGroupOpen(true)}
                  onClear={clearSelection}
                />
              )}
            </div>

            {/* Did you mean? */}
            {searchData?.pre_retrieval?.spell_checking.enabled &&
              searchData.pre_retrieval.spell_checking.alternative_query && (
                <p className="text-sm text-gray-500 mb-2">
                  Did you mean{' '}
                  <button
                    type="button"
                    onClick={() => {
                      const alt = searchData.pre_retrieval!.spell_checking.alternative_query!;
                      setQuery(alt);
                      updateSearchLocation(alt);
                      performSearch(alt);
                    }}
                    className="italic text-emerald-600 hover:text-emerald-700 hover:underline font-medium"
                  >
                    {searchData.pre_retrieval.spell_checking.alternative_query}
                  </button>
                  ?
                </p>
              )}

            {error && (
              <div className="mb-4 bg-red-50 text-red-700 border border-red-200 rounded-xl px-5 py-3 text-sm">
                {error}
              </div>
            )}

            {/* Results */}
            {groupingActive ? (
              groupedResults.length > 0 ? (
                <GroupedResults
                  groups={groupedResults}
                  viewMode={viewMode}
                  onDownload={handleDownload}
                  navigate={navigateFromSearch}
                  selection={selection}
                />
              ) : (
                <div className="flex flex-col items-center justify-center py-20 text-gray-400">
                  <SearchIcon className="w-12 h-12 mb-4 text-gray-300" />
                  <p className="text-lg font-medium text-gray-500">No grouped results found</p>
                  <p className="text-sm mt-1">Try a different grouping, query, or filter</p>
                </div>
              )
            ) : paginatedResults.length > 0 ? (
              <div className={viewMode === 'compact' ? 'space-y-1.5 mt-2' : 'space-y-3 mt-2'}>
                {paginatedResults.map((result) =>
                  viewMode === 'compact' ? (
                    <ResultRow
                      key={result.id}
                      result={result}
                      onDownload={handleDownload}
                      navigate={navigateFromSearch}
                      selection={selection}
                    />
                  ) : (
                    <ResultCard
                      key={result.id}
                      result={result}
                      onDownload={handleDownload}
                      navigate={navigateFromSearch}
                      selection={selection}
                    />
                  )
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-20 text-gray-400">
                <SearchIcon className="w-12 h-12 mb-4 text-gray-300" />
                <p className="text-lg font-medium text-gray-500">No results found</p>
                <p className="text-sm mt-1">Try a different query or adjust your filters</p>
              </div>
            )}

            {!groupingActive && (
              <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
            )}

            {/* Google-style notice for relevance-filtered (omitted) results */}
            {showOmittedResultsAction && (
              <div className="flex flex-col items-center gap-3 text-center py-10 border-t border-gray-100 mt-6">
                <p className="text-sm text-gray-500 max-w-md">
                  In order to show you the most relevant results, we have omitted some entries very
                  similar to the {displayedResultCount} already displayed.
                </p>
                <button
                  onClick={handleShowOmittedResults}
                  disabled={omittedResultsLoading}
                  className="flex items-center gap-1.5 text-sm font-medium text-white bg-gray-700 hover:bg-gray-800 disabled:opacity-60 rounded-lg px-4 py-2 transition-colors"
                  title="Show the results this search filtered out for relevance"
                >
                  {omittedResultsLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <FilterX className="w-4 h-4" />
                  )}
                  Show {omittedResultCount} filtered result{omittedResultCount !== 1 ? 's' : ''}
                </button>
                {omittedResultsError && (
                  <p className="text-sm text-red-700">{omittedResultsError}</p>
                )}
              </div>
            )}
          </div>
        </div>
      </main>

      {addToGroupOpen && selectedRecordIds.length > 0 && (
        <AddToGroupModal
          recordIds={selectedRecordIds}
          existingGroupIds={[]}
          onClose={() => setAddToGroupOpen(false)}
          onAdded={(group, counts) => {
            setAddToGroupOpen(false);
            addToast(addedToGroupMessage(counts), counts.addedCount === 0 ? 'info' : 'success', {
              link: { href: `/groups/${group.id}`, label: formatGroupLabel(group) },
            });
            setSelectedIds(new Set());
            setSelectionAnchorId(null);
          }}
        />
      )}

      <footer className="bg-white border-t border-gray-200 mt-auto py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-sm text-gray-500">
          <p>&copy; 2026 Palmerston North City Council &bull; ARA</p>
        </div>
      </footer>
    </div>
  );
}
